import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Loader2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shift = (ds, n) => {
  const d = new Date(ds + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Restrained status tones — subtle light background + colored text (same language as the Employees "Yes" badge).
const GROUPS = [
  { key: "full", labelKey: "att.full", solid: "#0071E3", pill: "bg-[#EEF3FA] text-[#0071E3]" },
  { key: "half", labelKey: "att.half", solid: "#E0A100", pill: "bg-[#FFF4E0] text-[#B4740A]" },
  { key: "absent", labelKey: "att.absent", solid: "#C8102E", pill: "bg-[#FDF0F2] text-[#C8102E]" },
  { key: "working", labelKey: "att.working", solid: "#1E7A34", pill: "bg-[#EAF7EE] text-[#1E7A34]" },
  { key: "needs", labelKey: "att.needs", solid: "#6E6E73", pill: "bg-[#F2F2F7] text-[#6E6E73]" },
  { key: "ignored", labelKey: "att.ignored", solid: "#8E8E93", pill: "bg-[#F2F2F7] text-[#8E8E93]" },
];
const STATUS = Object.fromEntries(GROUPS.map((g) => [g.key, g]));

export default function AttendanceDay() {
  const { t } = useT();
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/attendance/day-summary", { params: { date } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date]);

  const label = useMemo(() => {
    try {
      return new Date(date + "T00:00:00").toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch { return date; }
  }, [date]);

  const g = data?.groups || {};
  const total = GROUPS.reduce((a, x) => a + (g[x.key]?.length || 0), 0);

  const chips = [
    { key: "all", label: t("att.all"), count: total, solid: "#1D1D1F" },
    ...GROUPS.map((x) => ({ key: x.key, label: t(x.labelKey), count: g[x.key]?.length || 0, solid: x.solid })),
  ];

  const list = useMemo(() => {
    if (filter === "all") return GROUPS.flatMap((x) => (g[x.key] || []).map((p) => ({ ...p, _s: x.key })));
    return (g[filter] || []).map((p) => ({ ...p, _s: filter }));
  }, [filter, data]);

  return (
    <div data-testid="attendance-day-page" className="w-full space-y-4">
      <div>
        <h1 className="text-[22px] font-semibold tracking-tight sm:text-[26px]">{t("att.dayTitle")}</h1>
        <p className="mt-0.5 text-[13px] text-[#8E8E93]">{t("att.daySubtitle")}</p>
      </div>

      {/* Date toolbar */}
      <div className="rounded-2xl border border-[#E8E8ED] bg-white p-2.5">
        <div className="flex items-center gap-2">
          <button data-testid="att-day-prev" onClick={() => setDate(shift(date, -1))} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[#3A3A3C] transition-colors hover:bg-[#F2F2F7]">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <input
            data-testid="att-day-date"
            type="date"
            value={date}
            max={todayStr()}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="h-9 min-w-0 flex-1 rounded-xl border border-[#E1E1E6] bg-white px-3 text-[14px] focus:border-[#0071E3] focus:outline-none"
          />
          <button data-testid="att-day-next" onClick={() => setDate(shift(date, 1))} disabled={date >= todayStr()} className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[#3A3A3C] transition-colors hover:bg-[#F2F2F7] disabled:opacity-40">
            <ChevronRight className="h-5 w-5" />
          </button>
          <button data-testid="att-day-today" onClick={() => setDate(todayStr())} className="h-9 shrink-0 rounded-full bg-[#F2F2F7] px-4 text-[13px] font-medium text-[#1D1D1F] transition-colors hover:bg-[#E8E8ED]">
            {t("att.today")}
          </button>
        </div>
        <div className="mt-2 flex items-center justify-center gap-1.5 border-t border-[#F2F2F7] pt-2 text-[12.5px] font-medium text-[#6E6E73] sm:justify-start">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />{label}
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>
      ) : data?.before_golive ? (
        <div className="rounded-2xl border border-[#FDE8C4] bg-[#FFF8EC] p-6 text-center text-[14px] text-[#8A5A00]">{t("att.beforeGolive")}</div>
      ) : (
        <>
          {/* Neutral filter chips with counts */}
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {chips.map((c) => {
              const active = filter === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`att-day-tile-${c.key}`}
                  onClick={() => setFilter(c.key)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all active:scale-[0.97] ${active ? "text-white shadow-[0_2px_8px_rgba(0,0,0,0.12)]" : "bg-[#F2F2F7] text-[#6E6E73] hover:bg-[#E8E8ED]"}`}
                  style={active ? { background: c.solid } : undefined}
                >
                  {c.label}
                  <span className={`text-[12px] tabular-nums ${active ? "text-white/75" : "text-[#A1A1A6]"}`}>{c.count}</span>
                </button>
              );
            })}
          </div>

          {/* Clean list (Employees-style table) */}
          {list.length === 0 ? (
            <div className="grid place-items-center gap-2 rounded-[20px] border border-[#E8E8ED] bg-white py-16 text-center">
              <Users className="h-6 w-6 text-[#C7C7CC]" />
              <p className="text-[13.5px] text-[#B0B0B8]">{t("att.noneInGroup")}</p>
            </div>
          ) : (
            <div data-testid="att-day-list" className="overflow-hidden rounded-[20px] border border-[#E8E8ED] bg-white shadow-[0_10px_40px_rgba(0,0,0,0.04)]">
              <div className="hidden grid-cols-[1fr_140px_120px] gap-4 border-b border-[#F0F0F3] px-5 py-3 text-[12px] font-medium text-[#8E8E93] sm:grid">
                <span>Name</span>
                <span>Clock In / Out</span>
                <span className="text-right">Status</span>
              </div>
              <div className="divide-y divide-[#F0F0F3]">
                {list.map((p) => {
                  const meta = STATUS[p._s];
                  return (
                    <div
                      key={(p.employee_id || p.nip) + p._s}
                      data-testid={`att-day-person-${p.employee_id || p.nip}`}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[#FAFAFB] sm:grid sm:grid-cols-[1fr_140px_120px] sm:gap-4 sm:px-5"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <EmployeeAvatar id={p.employee_id} nama={p.nama} hasPhoto={p.has_photo} size={38} />
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 truncate text-[14px] font-semibold text-[#1D1D1F]"><EmployeeName id={p.employee_id} name={p.nama} /><JaritBadge show={p.is_jarit} /></p>
                          {p.nip && <p className="num text-[12px] text-[#8E8E93]">{p.nip}</p>}
                        </div>
                      </div>
                      <div className="num hidden text-[13px] text-[#3A3A3C] sm:block">
                        {p.clock_in || p.clock_out ? (
                          <>{p.clock_in || "--:--"} <span className="text-[#C7C7CC]">/</span> {p.clock_out || "--:--"}</>
                        ) : (
                          <span className="text-[#C7C7CC]">—</span>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-[12px] font-medium ${meta.pill}`}>{t(meta.labelKey)}</span>
                        {(p.clock_in || p.clock_out) && (
                          <p className="num mt-0.5 text-[11.5px] text-[#B0B0B8] sm:hidden">{p.clock_in || "--:--"} / {p.clock_out || "--:--"}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
